import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FileText, Plus, Trash2, Edit, Download, Send, Archive,
  LogOut, Home, Building2, Check, X, AlertCircle, User,
  Calendar, DollarSign, Percent, TrendingUp, ChevronRight,
  Search, Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReports() {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [properties, setProperties] = useState([]);
  const [view, setView] = useState(reportId ? "builder" : "list");
  const [currentReport, setCurrentReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  // Report builder state
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [exchangeAmount, setExchangeAmount] = useState("");
  const [exchangeDeadline, setExchangeDeadline] = useState("");
  const [reportTitle, setReportTitle] = useState("1031 Exchange Portfolio Recommendation");
  const [notes, setNotes] = useState("");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [marketFilter, setMarketFilter] = useState("all");
  const [saving, setSaving] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!response.data.is_admin) {
        navigate("/");
        return;
      }
      setUser(response.data);
    } catch (error) {
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) {
      fetchReports();
      fetchProperties();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (reportId && reports.length > 0) {
      const report = reports.find(r => r.report_id === reportId);
      if (report) {
        loadReportForEditing(report);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, reports]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`, { withCredentials: true });
      setReports(response.data);
    } catch (error) {
      logger.error("Failed to fetch reports:", error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`, { withCredentials: true });
      // Only show available properties
      setProperties(response.data.filter(p => p.status === "available"));
    } catch (error) {
      logger.error("Failed to fetch properties:", error);
    }
  };

  const loadReportForEditing = (report) => {
    setCurrentReport(report);
    setClientName(report.client_name);
    setClientEmail(report.client_email || "");
    setClientPhone(report.client_phone || "");
    setExchangeAmount(report.exchange_amount.toString());
    setExchangeDeadline(report.exchange_deadline || "");
    setReportTitle(report.report_title);
    setNotes(report.notes || "");
    setSelectedPropertyIds(report.properties.map(p => p.property_id));
    setView("builder");
  };

  const resetBuilder = () => {
    setCurrentReport(null);
    setClientName("");
    setClientEmail("");
    setClientPhone("");
    setExchangeAmount("");
    setExchangeDeadline("");
    setReportTitle("1031 Exchange Portfolio Recommendation");
    setNotes("");
    setSelectedPropertyIds([]);
    setSearchTerm("");
    setMarketFilter("all");
  };

  const handleNewReport = () => {
    resetBuilder();
    setView("builder");
    navigate("/admin/reports");
  };

  const handleBackToList = () => {
    resetBuilder();
    setView("list");
    navigate("/admin/reports");
  };

  const toggleProperty = (propertyId) => {
    setSelectedPropertyIds(prev => 
      prev.includes(propertyId) 
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const selectedProperties = properties.filter(p => selectedPropertyIds.includes(p.property_id));
  const totalInvestment = selectedProperties.reduce((sum, p) => sum + p.price, 0);
  const exchangeNum = parseFloat(exchangeAmount) || 0;
  const remainingFunds = exchangeNum - totalInvestment;
  const totalMonthlyRent = selectedProperties.reduce((sum, p) => sum + (p.projected_monthly_rent || 0), 0);
  const combinedCapRate = totalInvestment > 0 
    ? selectedProperties.reduce((sum, p) => sum + p.price * (p.cap_rate || 0), 0) / totalInvestment 
    : 0;

  const filteredProperties = properties.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMarket = marketFilter === "all" || p.market === marketFilter;
    return matchesSearch && matchesMarket;
  });

  const handleSaveReport = async (status = "draft") => {
    if (!clientName.trim()) {
      toast.error("Client name is required");
      return;
    }
    if (!exchangeAmount || parseFloat(exchangeAmount) <= 0) {
      toast.error("Valid exchange amount is required");
      return;
    }
    if (selectedPropertyIds.length === 0) {
      toast.error("Select at least one property");
      return;
    }

    setSaving(true);
    const data = {
      client_name: clientName,
      client_email: clientEmail || null,
      client_phone: clientPhone || null,
      exchange_amount: parseFloat(exchangeAmount),
      exchange_deadline: exchangeDeadline || null,
      property_ids: selectedPropertyIds,
      report_title: reportTitle,
      notes: notes || null
    };

    try {
      if (currentReport) {
        await axios.put(`${API}/reports/${currentReport.report_id}`, data, { withCredentials: true });
        toast.success("Report updated");
      } else {
        const response = await axios.post(`${API}/reports`, data, { withCredentials: true });
        setCurrentReport(response.data);
        toast.success("Report created");
      }
      fetchReports();
    } catch (error) {
      toast.error("Failed to save report");
      logger.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (reportId) => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `1031_Portfolio_Report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;
    try {
      await axios.delete(`${API}/reports/${reportId}`, { withCredentials: true });
      toast.success("Report deleted");
      fetchReports();
      if (currentReport?.report_id === reportId) {
        handleBackToList();
      }
    } catch (error) {
      toast.error("Failed to delete report");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/admin");
    } catch (error) {
      logger.error("Logout error:", error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const filteredReports = reports.filter(r => 
    statusFilter === "all" || r.status === statusFilter
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1A1A2E] text-white sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-bold">
                1031<span className="text-[#60A5FA]">Condo</span>
              </Link>
              <span className="text-gray-400">|</span>
              <span className="text-sm text-gray-300">Client Reports</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white">
                  <Home className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <Link to="/admin/properties">
                <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white">
                  <Building2 className="w-4 h-4 mr-2" />
                  Properties
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-300 hover:text-white">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {view === "list" ? (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* List Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#1A1A2E]">Client Reports</h1>
                  <p className="text-gray-500">{filteredReports.length} reports</p>
                </div>
                <div className="flex items-center gap-4">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Reports</SelectItem>
                      <SelectItem value="draft">Drafts</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleNewReport} className="btn-primary rounded-lg" data-testid="new-report-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Report
                  </Button>
                </div>
              </div>

              {/* Reports Grid */}
              {filteredReports.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600">No Reports Yet</h3>
                  <p className="text-gray-500 mb-4">Create your first client portfolio report</p>
                  <Button onClick={handleNewReport} className="btn-primary rounded-lg">
                    <Plus className="w-4 h-4 mr-2" />
                    New Report
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredReports.map((report) => (
                    <motion.div
                      key={report.report_id}
                      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                      whileHover={{ y: -2 }}
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-[#1A1A2E]">{report.client_name}</h3>
                            <p className="text-sm text-gray-500">{report.client_email || "No email"}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${
                            report.status === "draft" ? "bg-yellow-100 text-yellow-700" :
                            report.status === "sent" ? "bg-green-100 text-green-700" :
                            "bg-gray-100 text-gray-600"
                          }`}>
                            {report.status}
                          </span>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Exchange Amount:</span>
                            <span className="font-medium">{formatCurrency(report.exchange_amount)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Portfolio Total:</span>
                            <span className="font-medium">{formatCurrency(report.total_investment)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Properties:</span>
                            <span className="font-medium">{report.properties?.length || 0} units</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Cap Rate:</span>
                            <span className={`font-medium ${
                              report.combined_cap_rate >= 5 ? "text-green-600" :
                              report.combined_cap_rate >= 3 ? "text-yellow-600" : "text-red-600"
                            }`}>
                              {report.combined_cap_rate?.toFixed(2)}%
                            </span>
                          </div>
                        </div>

                        {/* Match indicator */}
                        <div className={`p-3 rounded-lg mb-4 ${
                          Math.abs(report.remaining_funds) < 1000 ? "bg-green-50 border border-green-200" :
                          report.remaining_funds > 0 ? "bg-yellow-50 border border-yellow-200" :
                          "bg-red-50 border border-red-200"
                        }`}>
                          <div className="flex items-center gap-2">
                            {Math.abs(report.remaining_funds) < 1000 ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-yellow-600" />
                            )}
                            <span className="text-sm font-medium">
                              {Math.abs(report.remaining_funds) < 1000 
                                ? "Exact Match!" 
                                : report.remaining_funds > 0 
                                  ? `${formatCurrency(report.remaining_funds)} under budget`
                                  : `${formatCurrency(Math.abs(report.remaining_funds))} over budget`
                              }
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => loadReportForEditing(report)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadPDF(report.report_id)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteReport(report.report_id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="builder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Builder Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <Button variant="ghost" onClick={handleBackToList} className="text-gray-500">
                    <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                    Back to Reports
                  </Button>
                  <h1 className="text-2xl font-bold text-[#1A1A2E]">
                    {currentReport ? "Edit Report" : "New Portfolio Report"}
                  </h1>
                </div>
                <div className="flex gap-3">
                  {currentReport && (
                    <Button
                      variant="outline"
                      onClick={() => handleDownloadPDF(currentReport.report_id)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  )}
                  <Button
                    onClick={() => handleSaveReport("draft")}
                    className="btn-primary"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : currentReport ? "Update Report" : "Save Report"}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {/* Left Column - Client Info & Property Selection */}
                <div className="col-span-2 space-y-6">
                  {/* Client Information */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-[#1A1A2E] mb-4 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Client Information
                    </h2>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                        <Input
                          value={clientName}
                          onChange={(e) => setClientName(e.target.value)}
                          placeholder="John Smith"
                          data-testid="client-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <Input
                          type="email"
                          value={clientEmail}
                          onChange={(e) => setClientEmail(e.target.value)}
                          placeholder="john@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <Input
                          value={clientPhone}
                          onChange={(e) => setClientPhone(e.target.value)}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">45-Day Deadline</label>
                        <Input
                          type="date"
                          value={exchangeDeadline}
                          onChange={(e) => setExchangeDeadline(e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">1031 Exchange Amount *</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            type="number"
                            value={exchangeAmount}
                            onChange={(e) => setExchangeAmount(e.target.value)}
                            placeholder="5000000"
                            className="pl-9"
                            data-testid="exchange-amount-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Property Selection */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-[#1A1A2E] mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Select Properties
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({selectedPropertyIds.length} selected)
                      </span>
                    </h2>
                    
                    {/* Search & Filter */}
                    <div className="flex gap-4 mb-4">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search properties..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <Select value={marketFilter} onValueChange={setMarketFilter}>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Market" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Markets</SelectItem>
                          <SelectItem value="nyc">NYC</SelectItem>
                          <SelectItem value="miami">Miami</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Property List */}
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {filteredProperties.map((property) => {
                        const isSelected = selectedPropertyIds.includes(property.property_id);
                        return (
                          <div
                            key={property.property_id}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected 
                                ? "border-blue-500 bg-blue-50" 
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            onClick={() => toggleProperty(property.property_id)}
                            data-testid={`property-select-${property.property_id}`}
                          >
                            <div className="flex items-center gap-4">
                              {/* Checkbox */}
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected ? "bg-blue-500 border-blue-500" : "border-gray-300"
                              }`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              
                              {/* Property Image */}
                              <div className="w-16 h-12 rounded overflow-hidden bg-gray-100">
                                {property.images?.[0] ? (
                                  <img src={property.images[0]} alt={property.title} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-gray-300" />
                                  </div>
                                )}
                              </div>
                              
                              {/* Property Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-[#1A1A2E] truncate">{property.title}</h4>
                                  <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                                    property.market === "nyc" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"
                                  }`}>
                                    {property.market === "nyc" ? "NYC" : "Miami"}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500">{property.location}</p>
                              </div>
                              
                              {/* Price & Stats */}
                              <div className="text-right">
                                <p className="font-semibold text-[#1A1A2E]">{formatCurrency(property.price)}</p>
                                <p className="text-xs text-gray-500">
                                  {property.bedrooms}bd/{property.bathrooms}ba • {property.cap_rate?.toFixed(1)}% cap
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-[#1A1A2E] mb-4">Report Notes</h2>
                    <Input
                      value={reportTitle}
                      onChange={(e) => setReportTitle(e.target.value)}
                      placeholder="Report Title"
                      className="mb-4"
                    />
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes or recommendations for the client..."
                      rows={4}
                    />
                  </div>
                </div>

                {/* Right Column - Portfolio Summary */}
                <div className="space-y-6">
                  {/* Exchange Target */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="font-semibold text-[#1A1A2E] mb-4">Exchange Target</h3>
                    <div className="text-3xl font-bold text-[#1A1A2E] mb-2">
                      {formatCurrency(exchangeNum)}
                    </div>
                    <p className="text-sm text-gray-500">1031 Exchange Funds</p>
                  </div>

                  {/* Portfolio Total */}
                  <div className={`rounded-xl border-2 p-6 ${
                    Math.abs(remainingFunds) < 1000 && exchangeNum > 0
                      ? "bg-green-50 border-green-300"
                      : remainingFunds >= 0
                        ? "bg-white border-gray-200"
                        : "bg-red-50 border-red-300"
                  }`}>
                    <h3 className="font-semibold text-[#1A1A2E] mb-4">Portfolio Total</h3>
                    <div className="text-3xl font-bold text-[#1A1A2E] mb-2">
                      {formatCurrency(totalInvestment)}
                    </div>
                    
                    {/* Progress Bar */}
                    {exchangeNum > 0 && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>0%</span>
                          <span>100%</span>
                        </div>
                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              totalInvestment > exchangeNum ? "bg-red-500" :
                              Math.abs(remainingFunds) < 1000 ? "bg-green-500" : "bg-blue-500"
                            }`}
                            style={{ width: `${Math.min(100, (totalInvestment / exchangeNum) * 100)}%` }}
                          />
                        </div>
                        <div className={`mt-3 p-3 rounded-lg ${
                          Math.abs(remainingFunds) < 1000 
                            ? "bg-green-100" 
                            : remainingFunds > 0 
                              ? "bg-yellow-100" 
                              : "bg-red-100"
                        }`}>
                          <div className="flex items-center gap-2">
                            {Math.abs(remainingFunds) < 1000 ? (
                              <Check className="w-5 h-5 text-green-600" />
                            ) : remainingFunds > 0 ? (
                              <AlertCircle className="w-5 h-5 text-yellow-600" />
                            ) : (
                              <X className="w-5 h-5 text-red-600" />
                            )}
                            <span className="font-medium">
                              {Math.abs(remainingFunds) < 1000 
                                ? "Perfect Match!" 
                                : remainingFunds > 0 
                                  ? `${formatCurrency(remainingFunds)} remaining`
                                  : `${formatCurrency(Math.abs(remainingFunds))} over budget`
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Portfolio Stats */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="font-semibold text-[#1A1A2E] mb-4">Portfolio Metrics</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Properties</span>
                        <span className="font-semibold">{selectedPropertyIds.length} units</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Combined Cap Rate</span>
                        <span className={`font-semibold ${
                          combinedCapRate >= 5 ? "text-green-600" :
                          combinedCapRate >= 3 ? "text-yellow-600" : "text-red-600"
                        }`}>
                          {combinedCapRate.toFixed(2)}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Monthly Rent</span>
                        <span className="font-semibold text-green-600">{formatCurrency(totalMonthlyRent)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Annual Rent</span>
                        <span className="font-semibold text-green-600">{formatCurrency(totalMonthlyRent * 12)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Selected Properties Summary */}
                  {selectedProperties.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <h3 className="font-semibold text-[#1A1A2E] mb-4">Selected Properties</h3>
                      <div className="space-y-3">
                        {selectedProperties.map((prop) => (
                          <div key={prop.property_id} className="flex justify-between items-center text-sm">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${
                                prop.market === "nyc" ? "bg-blue-500" : "bg-orange-500"
                              }`} />
                              <span className="truncate max-w-[150px]">{prop.title}</span>
                            </div>
                            <span className="font-medium">{formatCurrency(prop.price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Disclaimer */}
              <div className="col-span-3 mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 leading-relaxed">
                  <strong>Disclaimer:</strong> All potential sale values displayed are estimated and subject to market conditions. 
                  All rental income projections and expense estimates are approximations based on current market data and may vary. 
                  Cap rates and financial metrics are calculated using estimated figures and should not be considered projections of future performance. 
                  This calculator and report are for demonstration and informational purposes only and do not constitute financial, 
                  tax, or legal advice. Please consult with qualified professionals regarding your specific 1031 exchange situation.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
